import React, { useState, useEffect, useRef } from "react";
import { useMutation } from "@tanstack/react-query";
import { apiFetch } from "../api";

interface Message {
  role: "user" | "assistant";
  content: string;
  timestamp?: string;
}

interface ChatPreviewProps {
  agentId: string;
  agentName: string;
  model: string;
  accentColor?: string;
  theme?: string;
  icon?: string;
}

// Utility function to detect if a color is light or dark
function isLightColor(hex: string): boolean {
  // Remove # if present
  const color = hex.replace('#', '');
  
  // Convert to RGB
  const r = parseInt(color.substring(0, 2), 16);
  const g = parseInt(color.substring(2, 4), 16);
  const b = parseInt(color.substring(4, 6), 16);
  
  // Calculate relative luminance
  const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  
  // If luminance > 0.5, it's a light color
  return luminance > 0.5;
}

export function ChatPreview({ agentId, agentName, model, accentColor = "#137fec", theme = "dark", icon = "chat" }: ChatPreviewProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [responseTime, setResponseTime] = useState<number | null>(null);
  const [contextCount, setContextCount] = useState(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const testChatMutation = useMutation({
    mutationFn: ({
      message,
      conversationId: convId,
    }: {
      message: string;
      conversationId: string | null;
    }) =>
      apiFetch<{
        reply: string;
        conversation_id: string;
        response_time_ms?: number;
        context_count?: number;
      }>(`/api/agents/${agentId}/test-chat`, {
        method: "POST",
        body: JSON.stringify({
          message,
          conversation_id: convId,
          visitor_id: `preview-${agentId}`,
        }),
      }),
  });

  const loading = testChatMutation.isPending;

  // Determine text color based on accent color contrast
  const isAccentLight = isLightColor(accentColor);
  const accentTextColor = isAccentLight ? "#1e293b" : "#ffffff";
  const accentTextColorMuted = isAccentLight ? "rgba(30, 41, 59, 0.7)" : "rgba(255, 255, 255, 0.7)";

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  async function handleSend() {
    if (!input.trim() || loading) return;

    const userMessage: Message = {
      role: "user",
      content: input.trim(),
      timestamp: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput("");

    try {
      const data = await testChatMutation.mutateAsync({
        message: userMessage.content,
        conversationId,
      });

      setConversationId(data.conversation_id);
      if (data.response_time_ms !== undefined) setResponseTime(data.response_time_ms);
      if (data.context_count !== undefined) setContextCount(data.context_count);

      const assistantMessage: Message = {
        role: "assistant",
        content: data.reply,
        timestamp: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, assistantMessage]);
    } catch (err: any) {
      const errorMessage: Message = {
        role: "assistant",
        content: `Error: ${err.message || "Failed to send message"}`,
        timestamp: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    }
  }

  function handleRefresh() {
    setMessages([]);
    setConversationId(null);
    setResponseTime(null);
    setContextCount(0);
  }

  function formatTime(timestamp: string) {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return "Just now";
    if (diffMins === 1) return "1 min ago";
    return `${diffMins} min ago`;
  }

  return (
    <>
      <div className={`chat-preview ${theme === "light" ? "chat-preview-light" : "chat-preview-dark"}`}>
        <div className="chat-preview-header" style={{ background: accentColor, color: accentTextColor }}>
          <div className="chat-preview-header-left">
            <div className="chat-preview-icon" style={{ background: accentColor }}>
              <span className="material-symbols-outlined" style={{ color: accentTextColor }}>{icon}</span>
            </div>
            <div className="chat-preview-title">
              <span className="chat-preview-name" style={{ color: accentTextColor }}>Preview: {agentName}</span>
              <div className="chat-preview-live">
                <span className="live-indicator"></span>
                <span className="live-text" style={{ color: accentTextColorMuted }}>Live Testing</span>
              </div>
            </div>
          </div>
          <div className="chat-preview-header-right">
            <button 
              className="chat-refresh-btn" 
              onClick={handleRefresh} 
              title="Reset conversation" 
              style={{ 
                color: accentTextColor,
                background: isAccentLight ? "rgba(0, 0, 0, 0.05)" : "transparent"
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = isAccentLight ? "rgba(0, 0, 0, 0.1)" : "rgba(255, 255, 255, 0.15)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = isAccentLight ? "rgba(0, 0, 0, 0.05)" : "transparent";
              }}
            >
              <span className="material-symbols-outlined">refresh</span>
            </button>
          </div>
        </div>

        <div className="chat-messages">
          {messages.length === 0 && (
            <div className="chat-empty">
              <p>Start a conversation to test your agent</p>
            </div>
          )}
          {messages.map((msg, idx) => (
            <div key={idx} className={`chat-message chat-message-${msg.role}`}>
              <div className="chat-avatar">
                {msg.role === "user" ? (
                  <span className="material-symbols-outlined">person</span>
                ) : (
                  <span className="material-symbols-outlined">{icon}</span>
                )}
              </div>
              <div className="chat-content">
                <div className="chat-text" style={msg.role === "user" ? { background: accentColor, borderColor: accentColor, color: accentTextColor } : {}}>
                  <p>{msg.content}</p>
                  {msg.timestamp && (
                    <span className="chat-timestamp" style={msg.role === "user" ? { color: accentTextColorMuted } : {}}>{formatTime(msg.timestamp)}</span>
                  )}
                </div>
              </div>
            </div>
          ))}
          {loading && (
            <div className="chat-message chat-message-assistant">
              <div className="chat-avatar">
                <span className="material-symbols-outlined">{icon}</span>
              </div>
              <div className="chat-content">
                <div className="chat-typing">Thinking...</div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <div className="chat-input-area">
          <div className="chat-input-wrapper">
            <input
              type="text"
              className="chat-input"
              placeholder="Type a message to test..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && handleSend()}
              disabled={loading}
            />
            <button
              className="chat-send-btn"
              onClick={handleSend}
              disabled={loading || !input.trim()}
              style={{ background: accentColor, color: accentTextColor }}
            >
              <span className="material-symbols-outlined">send</span>
            </button>
          </div>
          <div className="chat-info-bar">
            <span>
              <span className="material-symbols-outlined">bolt</span>
              Powered by {model}
            </span>
            <span>
              <span className="material-symbols-outlined">history</span>
              Context: {contextCount} messages
            </span>
          </div>
        </div>
      </div>

      <div className="chat-debug-footer">
        <div className="debug-badges">
          <span className="debug-badge">Debug Mode</span>
          <span className="debug-status">Connected</span>
        </div>
        {responseTime !== null ? (
          <span className="debug-time">Chat response time: ~{(responseTime / 1000).toFixed(1)}s</span>
        ) : (
          <span className="debug-time">Chat response time: ~1.2s</span>
        )}
      </div>
    </>
  );
}
